import { inject, Injectable, Signal } from '@angular/core';
import {
  AuthenticatedResult,
  LoginResponse,
  LogoutAuthOptions,
  OidcSecurityService,
} from 'angular-auth-oidc-client';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { AuthTenantGroupData } from './auth-tenant-group-data.model';
import { LocalStorageService } from '@shared/storage/local-storage.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _oidcSecurityService = inject(OidcSecurityService);
  private readonly _localStorage = inject(LocalStorageService);

  private readonly _selectedTenantUIdKey = 'selectedTenantUId';
  private readonly _userIdKey = 'userId';

  private readonly _selectedMandant: Subject<string | undefined> = new Subject<
    string | undefined
  >();

  // Keep authentication state
  private readonly _isAuthenticated$ = new BehaviorSubject<LoginResponse>(
    undefined,
  );

  // Expose authentication state as Observable
  public readonly isAuthenticated$: Observable<LoginResponse> =
    this._isAuthenticated$.asObservable();

  public readonly authenticated: Signal<AuthenticatedResult> =
    this._oidcSecurityService.authenticated;

  public set selectedTenantUId(tenantUId: string | undefined) {
    if (tenantUId) {
      this.authMandants().then((pTokenMandats: AuthTenantGroupData[]) => {
        if (pTokenMandats.find((item) => item.id === tenantUId)) {
          //existiert im token
          this._localStorage.set(this._selectedTenantUIdKey, tenantUId);
          this._selectedMandant.next(tenantUId);
        }
      });
    } else {
      this._localStorage.remove(this._selectedTenantUIdKey);
      this._selectedMandant.next(tenantUId);
    }
  }

  constructor() {
    // Wrap the checkAuth() subscription
    this._oidcSecurityService.checkAuth().subscribe({
      next: (authResult) => {
        // authResult is boolean: true if authenticated, false otherwise
        this._isAuthenticated$.next(authResult);
        if (authResult?.isAuthenticated) {
          this._persistUserId();
        }
      },
      error: (err) => {
        console.error('Auth check failed', err);
        this._isAuthenticated$.next(undefined);
      },
    });
  }

  public login() {
    this._oidcSecurityService.authorize();
  }

  public logout(
    configId?: string,
    logoutAuthOptions?: LogoutAuthOptions,
  ): Observable<unknown> {
    return this._oidcSecurityService.logoffAndRevokeTokens(
      configId,
      logoutAuthOptions,
    );
  }

  public getAccessToken(configId?: string): Observable<string> {
    return this._oidcSecurityService.getAccessToken(configId);
  }

  /** get id token */
  public getIdToken(configId?: string): Observable<string> {
    return this._oidcSecurityService.getIdToken(configId);
  }

  /** get selected tenant */
  public async getSelectedTenantUId(): Promise<string | null> {
    const tenant = await this._localStorage.get(this._selectedTenantUIdKey);
    return tenant ?? null;
  }

  /** get mandants the user is assigned to */
  public async authMandants(): Promise<AuthTenantGroupData[]> {
    const idTokenData = await lastValueFrom(
      this._oidcSecurityService.getPayloadFromIdToken(
        false,
        environment.auth.configId,
      ),
    );
    if (idTokenData && idTokenData['tenants']) {
      const result: AuthTenantGroupData[] = JSON.parse(idTokenData['tenants']);
      return result ? result : [];
    }
    return [];
  }

  public getUserData(): Observable<unknown> {
    return this._oidcSecurityService.getUserData(environment.auth.configId);
  }

  public async getUserEmail(): Promise<string> {
    const userData = await lastValueFrom(this.getUserData());
    return userData['email'] ? userData['email'] : '';
  }

  /** get user name */
  public async authFirstname(): Promise<string> {
    const idToken = await lastValueFrom(this.getIdToken());
    if (idToken) {
      const decodedToken = this.decodeJwt(idToken);
      return decodedToken['name'] ? decodedToken['name'] : '';
    }
    return '';
  }

  /** get user name */
  public async authLastname(): Promise<string> {
    const idToken = await lastValueFrom(this.getIdToken());
    if (idToken) {
      const decodedToken = this.decodeJwt(idToken);
      return decodedToken['family_name'] ? decodedToken['family_name'] : '';
    }
    return '';
  }

  public async getProfileInitials(): Promise<string> {
    const firstName = await this.authFirstname();
    const lastName = await this.authLastname();

    let initials = '';

    if (firstName && firstName.length > 0) {
      initials += firstName.charAt(0).toUpperCase();
    }

    if (lastName && lastName.length > 0) {
      initials += lastName.charAt(0).toUpperCase();
    }

    if (!initials || initials.length === 0) {
      const email = await this.getUserEmail();
      if (email && email.length > 0) {
        initials += email.charAt(0).toUpperCase();
      }
    }

    return initials;
  }

  public async getUserId(): Promise<string | null> {
    const userId = await this._localStorage.get(this._userIdKey);
    return userId ?? null;
  }

  private _persistUserId(): void {
    lastValueFrom(this.getIdToken()).then((idToken) => {
      if (idToken) {
        const decoded = this.decodeJwt(idToken) as Record<string, string>;
        const sub = decoded['sub'];
        if (sub) {
          this._localStorage.set(this._userIdKey, sub);
        }
      }
    });
  }

  private decodeJwt(token: string): unknown {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  }
}
